import {definePreset} from '@openng/optimus-ui-themes';
import {AuraCopy} from './aura-preset';

export const optimusPreset = definePreset(AuraCopy, {
  semantic: {
    primary: {
      50: 'var(--color-blue-50)',
      100: 'var(--color-blue-100)',
      200: 'var(--color-blue-200)',
      300: 'var(--color-blue-300)',
      400: 'var(--color-blue-400)',
      500: 'var(--color-blue-500)',
      600: 'var(--color-blue-600)',
      700: 'var(--color-blue-700)',
      800: 'var(--color-blue-800)',
      900: 'var(--color-blue-900)',
      950: 'var(--color-blue-950)',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.700}',
          hoverColor: '{primary.800}',
          activeColor: '{primary.800}',
        },
      },
      dark: {
        primary: {
          color: '{primary.600}',
          contrastColor: 'var(--color-white)',
          hoverColor: '{primary.700}',
          activeColor: '{primary.700}',
        },
        surface: {
          0: 'var(--color-white)',
          50: 'var(--color-gray-50)',
          100: 'var(--color-gray-100)',
          200: 'var(--color-gray-200)',
          300: 'var(--color-gray-300)',
          400: 'var(--color-gray-400)',
          500: 'var(--color-gray-500)',
          600: 'var(--color-gray-600)',
          700: 'var(--color-gray-700)',
          800: 'var(--color-gray-800)',
          900: 'var(--color-gray-800)',
          950: 'var(--color-gray-900)',
        },
      },
    },
  },
  components: {
    drawer: {
      header: {
        padding: '0.75rem {overlay.modal.padding}',
      },
      content: {
        padding: '0.75rem {overlay.modal.padding} {overlay.modal.padding} {overlay.modal.padding}',
      },
      colorScheme: {
        dark: {
          root: {
            background: 'var(--color-neutral-primary-soft)',
          },
        },
      },
    },
    datatable: {
      colorScheme: {
        light: {
          root: {
            borderColor: 'var(--color-default-medium)',
          },
        },
        dark: {
          root: {
            borderColor: 'var(--color-default-medium)',
          },
        },
      },
    },
    datepicker: {
      dropdown: {
        background: 'var(--color-neutral-secondary-medium)',
        borderColor: 'var(--color-default-medium)',
      },
      css: `
        .p-datepicker {
          font-size: 0.875rem;
          border: 1px solid var(--color-default-medium);
          border-radius: var(--radius-base);
          background: var(--color-neutral-secondary-medium);
        }

        .p-datepicker .p-datepicker-input {
          border: none;
          box-shadow: none;
          background: transparent;
        }

        .p-datepicker .p-inputtext:focus:not(:disabled),
        .p-datepicker .p-inputtext.p-invalid:focus:not(:disabled) {
          box-shadow: none;
        }

        .p-datepicker:has(.p-datepicker-input.p-invalid):not(.p-disabled),
        .p-datepicker:has(.p-datepicker-input.p-invalid):not(.p-disabled):hover {
          background: var(--color-danger-soft);
          border-color: var(--color-danger-subtle);
        }

        .p-datepicker .p-datepicker-dropdown:not(:disabled),
        .p-datepicker .p-datepicker-dropdown:not(:disabled):hover {
          color: var(--p-form-field-icon-color);
          background: transparent;
          border-color: transparent;
        }

        .p-datepicker.p-focus:not(.p-disabled) {
          border-color: var(--color-brand);
          box-shadow: 0 0 0 0px #fff, 0 0 0 1px var(--color-brand), 0 1px 2px 0px rgba(0, 0, 0, 0.05);
        }
          
        .p-datepicker:has(.p-datepicker-input.p-invalid).p-focus:not(.p-disabled) {
          border-color: var(--color-danger);
          box-shadow: 0 0 0 0px #fff, 0 0 0 1px var(--color-danger), 0 1px 2px 0px rgba(0, 0, 0, 0.05);
        }
      `,
    },
    inputtext: {
      root: {
        background: 'var(--color-neutral-secondary-medium)',
        borderColor: 'var(--color-default-medium)',
        hoverBorderColor: 'var(--color-default-medium)',
        borderRadius: 'var(--radius-base)',
        disabledBackground: 'var(--color-neutral-tertiary-medium)',
        disabledColor: 'var(--color-body-subtle)',
      },
      css: `
        .p-inputtext {
          font-size: 0.875rem;
        }
        .p-inputtext.p-invalid:not(:disabled)::placeholder {
          color: var(--color-fg-danger-strong);
        }
      `,
    },
    multiselect: {
      root: {
        background: 'var(--color-neutral-secondary-medium)',
        borderColor: 'var(--color-default-medium)',
        hoverBorderColor: 'var(--color-default-medium)',
        focusBorderColor: 'var(--color-brand)',
        invalidBorderColor: 'var(--color-danger-subtle)',
        borderRadius: 'var(--radius-base)',
        disabledBackground: 'var(--color-neutral-tertiary-medium)',
        disabledColor: 'var(--color-body-subtle)',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: '0 0 0 0px #fff, 0 0 0 1px var(--color-brand), 0 1px 2px 0px rgba(0, 0, 0, 0.05)',
        },
      },
      chip: {
        borderRadius: 'var(--radius-base)',
      },
      css: `
        .p-multiselect {
          font-size: 0.875rem;
        }
        .p-multiselect-label {
          flex-wrap: wrap;
          display: inline-flex;
        }
        .p-multiselect.p-invalid:not(.p-disabled),
        .p-multiselect.p-invalid:not(.p-disabled):hover {
          background: var(--color-danger-soft);
          border-color: var(--color-danger-subtle);
        }
        .p-multiselect.p-invalid .p-multiselect-label,
        .p-multiselect.p-invalid .p-multiselect-label.p-placeholder {
          color: var(--color-fg-danger-strong);
        }
        .p-multiselect.p-invalid.p-focus:not(.p-disabled) {
          border-color: var(--color-danger);
          box-shadow: 0 0 0 0px #fff, 0 0 0 1px var(--color-danger), 0 1px 2px 0px rgba(0, 0, 0, 0.05);
        }
      `,
    },
    autocomplete: {
      root: {
        borderRadius: 'var(--radius-base)',
        disabledBackground: 'var(--color-neutral-tertiary-medium)',
        disabledColor: 'var(--color-body-subtle)',
        invalidPlaceholderColor: 'var(--color-fg-danger-strong)',
      },
      css: `
        .p-autocomplete {
          font-size: 0.875rem;
          border: 1px solid var(--color-default-medium);
          border-radius: var(--radius-base);
          background: var(--color-neutral-secondary-medium);
        }

        .p-autocomplete-option {
          white-space: normal !important;
          word-break: break-word;
          font-size: 0.875rem;
        }

        .p-autocomplete .p-autocomplete-input {
          border: none;
          box-shadow: none;
          background: transparent;
        }

        .p-autocomplete .p-inputtext:focus:not(:disabled),
        .p-autocomplete .p-inputtext.p-invalid:focus:not(:disabled) {
          box-shadow: none;
        }

        .p-autocomplete.p-invalid:not(.p-disabled),
        .p-autocomplete.p-invalid:not(.p-disabled):hover {
          background: var(--color-danger-soft);
          border-color: var(--color-danger-subtle);
        }

        .p-autocomplete .p-autocomplete-dropdown:not(:disabled),
        .p-autocomplete .p-autocomplete-dropdown:not(:disabled):hover {
          color: var(--p-form-field-icon-color);
          background: transparent;
          border-color: transparent;
        }

        .p-autocomplete.p-focus:not(.p-disabled) {
          border-color: var(--color-brand);
          box-shadow: 0 0 0 0px #fff, 0 0 0 1px var(--color-brand), 0 1px 2px 0px rgba(0, 0, 0, 0.05);
        }

        .p-autocomplete.p-invalid.p-focus:not(.p-disabled) {
          border-color: var(--color-danger);
          box-shadow: 0 0 0 0px #fff, 0 0 0 1px var(--color-danger), 0 1px 2px 0px rgba(0, 0, 0, 0.05);
        }
      `,
    },
    select: {
      root: {
        background: 'var(--color-neutral-secondary-medium)',
        borderColor: 'var(--color-default-medium)',
        hoverBorderColor: 'var(--color-default-medium)',
        focusBorderColor: 'var(--color-brand)',
        invalidBorderColor: 'var(--color-danger-subtle)',
        borderRadius: 'var(--radius-base)',
        disabledBackground: 'var(--color-neutral-tertiary-medium)',
        disabledColor: 'var(--color-body-subtle)',
        focusRing: {
          width: '0',
          style: 'none',
          color: 'transparent',
          offset: '0',
          shadow: '0 0 0 0px #fff, 0 0 0 1px var(--color-brand), 0 1px 2px 0px rgba(0, 0, 0, 0.05)',
        },
      },
      css: `
        .p-select, .p-select-label {
          font-size: 0.875rem;
        }
        .p-select.p-invalid:not(.p-disabled),
        .p-select.p-invalid:not(.p-disabled):hover {
          background: var(--color-danger-soft);
          border-color: var(--color-danger-subtle);
        }
        .p-select.p-invalid .p-select-label,
        .p-select.p-invalid .p-select-label.p-placeholder {
          color: var(--color-fg-danger-strong);
        }
        .p-select.p-invalid.p-focus:not(.p-disabled) {
          border-color: var(--color-danger);
          box-shadow: 0 0 0 0px #fff, 0 0 0 1px var(--color-danger), 0 1px 2px 0px rgba(0, 0, 0, 0.05);
        }
      `,
    },
    chip: {
      colorScheme: {
        light: {
          root: {
            background: 'var(--color-brand-softer)',
            color: 'var(--color-fg-brand)',
          },
          icon: {
            color: 'var(--color-fg-brand)',
          },
          removeIcon: {
            color: 'var(--color-fg-disabled)',
          },
        },
        dark: {
          root: {
            background: 'var(--color-brand-softer)',
            color: 'var(--color-fg-brand)',
          },
          icon: {
            color: 'var(--color-fg-brand)',
          },
          removeIcon: {
            color: 'var(--color-fg-disabled)',
          },
        },
      },
    },
    tooltip: {
      css: `
        .p-tooltip {
          font-size: 0.85rem;
        }
      `,
    },
  },
});
